import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Search, Plus, Eye, Download, Printer } from 'lucide-react';
import { BillingSection } from './BillingModule';
import { InvoiceModal } from './InvoiceModal';
import { Invoice } from '../../types';
interface Props {
  filter: BillingSection;
  onNavigate: (section: BillingSection) => void;
}
export const Invoices: React.FC<Props> = ({
  filter,
  onNavigate
}) => {
  const {
    invoices,
    patients,
    walkInClients
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);
  const getStatusFilter = () => {
    switch (filter) {
      case 'invoices-draft':
        return 'Draft';
      case 'invoices-sent':
        return 'Sent';
      case 'invoices-unpaid':
        return 'Unpaid';
      case 'invoices-paid':
        return 'Paid';
      case 'invoices-partial':
        return 'Partially Paid';
      case 'invoices-overdue':
        return 'Overdue';
      default:
        return 'All';
    }
  };
  const statusFilter = getStatusFilter();
  const filteredInvoices = invoices.filter(inv => {
    if (statusFilter !== 'All' && inv.status !== statusFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (inv.invoiceNumber.toLowerCase().includes(q)) return true;
    return getCustomerName(inv.customerId, inv.customerType, inv.customerName).toLowerCase().includes(q);
  });
  function getCustomerName(customerId: string, type: 'Patient' | 'WalkIn', savedName?: string) {
    if (type === 'Patient') {
      const p = patients.find(p => p.id === customerId);
      return p ? `${p.firstName} ${p.lastName}` : 'Unknown Patient';
    }
    return savedName || walkInClients.find(c => c.id === customerId)?.name || 'Walk-in client';
  }
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZM', {
      style: 'currency',
      currency: 'ZMW'
    }).format(amount);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Unpaid':
        return 'bg-amber-100 text-amber-800';
      case 'Partially Paid':
        return 'bg-blue-100 text-blue-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Sent':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getTitle = () => {
    switch (filter) {
      case 'invoices-draft':
        return 'Draft Invoices';
      case 'invoices-sent':
        return 'Sent Invoices';
      case 'invoices-unpaid':
        return 'Unpaid Invoices';
      case 'invoices-paid':
        return 'Paid Invoices';
      case 'invoices-partial':
        return 'Partially Paid Invoices';
      case 'invoices-overdue':
        return 'Overdue Invoices';
      default:
        return 'Invoices';
    }
  };
  return <div className="space-y-6" data-id="element-1224">
      <div className="flex justify-between items-center" data-id="element-1225">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1226">{getTitle()}</h1>
        <Button onClick={() => onNavigate('new-invoice')} data-id="element-1227">
          <Plus className="w-4 h-4 mr-2" data-id="element-1228" /> New Invoice
        </Button>
      </div>

      <Card noPadding data-id="element-1229">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1230">
          <div className="relative max-w-md" data-id="element-1231">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1232">
              <Search className="h-5 w-5 text-gray-400" data-id="element-1233" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by invoice number or customer name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1234" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-1235">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1236">
            <thead className="bg-gray-50" data-id="element-1237">
              <tr data-id="element-1238">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1239">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1240">
                  Invoice#
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1241">
                  Customer
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1242">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1243">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1244">
                  Balance Due
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1245">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1246">
              {filteredInvoices.length > 0 ? filteredInvoices.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(inv => <tr key={inv.id} className="hover:bg-gray-50" data-id="element-1247">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1248">
                        {new Date(inv.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-1249">
                        {inv.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1250">
                        {getCustomerName(inv.customerId, inv.customerType, inv.customerName)}
                        <span className="ml-2 text-xs text-gray-400 font-normal" data-id="element-1251">
                          ({inv.customerType})
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-1252">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(inv.status)}`} data-id="element-1253">
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900" data-id="element-1254">
                        {formatCurrency(inv.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-red-600" data-id="element-1255">
                        {formatCurrency(inv.balanceDue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1256">
                        <button className="text-gray-400 hover:text-blue-600 mx-1" title="View" onClick={() => setViewingInvoice(inv)} data-id="element-1257">
                          <Eye className="w-4 h-4" data-id="element-1258" />
                        </button>
                      </td>
                    </tr>) : <tr data-id="element-1259">
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500" data-id="element-1260">
                    No invoices found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {viewingInvoice && <InvoiceModal invoice={viewingInvoice} onClose={() => setViewingInvoice(null)} data-id="element-1261" />}
    </div>;
};